import {
	AccommodationIcon,
	BusIcon,
	CheckIcon,
	ExpandMoreIcon,
	LandmarkIcon,
	MoreHorizIcon,
	ParkIcon,
	RestaurantIcon,
	ShoppingBagIcon,
	TicketIcon,
} from '@/assets/icons'
import { DEFAULT_EVENT_CATEGORY, EVENT_CATEGORIES, EVENT_CATEGORY_LABELS } from '@/constants/eventCategories'
import { backgroundOrange } from '@/theme/colors'
import { iconSizes } from '@/theme/tokens'
import { DropdownChevron, DropdownTrigger } from '@components/Dropdown'
import Modal from '@components/Modal'
import { OptionRow } from '@components/OptionRow'
import { EventCategory } from '@orbit/api-types'
import React, { useState } from 'react'
import { CategoryTile, FieldValue, RowCheck, RowName, SheetList } from './CategoryField.styles'

/** Each category's glyph — the web's stand-ins for the iOS symbols. */
const GLYPHS: Record<EventCategory, React.ElementType> = {
	FoodAndDrink: RestaurantIcon,
	SeeAndDo: LandmarkIcon,
	Nature: ParkIcon,
	Shopping: ShoppingBagIcon,
	Accommodation: AccommodationIcon,
	Transportation: BusIcon,
	Event: TicketIcon,
	Other: MoreHorizIcon,
}

/** A category's glyph on its tile: orange, or grey for the catch-all Other. */
export const CategoryGlyph: React.FC<{ category: EventCategory }> = ({ category }) => {
	const Glyph = GLYPHS[category]
	return (
		<CategoryTile $muted={category === DEFAULT_EVENT_CATEGORY} aria-hidden>
			<Glyph sx={{ fontSize: iconSizes.sm }} />
		</CategoryTile>
	)
}

interface CategorySheetProps {
	isOpen: boolean
	selected: EventCategory
	onSelect: (category: EventCategory) => void
	onClose: () => void
}

/** The category picker: every category as a row with its glyph. A tap picks and closes; there is no confirm. */
export const CategorySheet: React.FC<CategorySheetProps> = ({ isOpen, selected, onSelect, onClose }) => (
	<Modal isOpen={isOpen} onClose={onClose} title="Choose Category" size="small" backgroundColor={backgroundOrange}>
		<SheetList role="listbox" aria-label="Categories">
			{EVENT_CATEGORIES.map(category => (
				<OptionRow
					key={category}
					type="button"
					role="option"
					aria-selected={category === selected}
					$selected={category === selected}
					onClick={() => {
						// Applied first, so the field underneath already shows it as the sheet goes.
						onSelect(category)
						onClose()
					}}
				>
					<CategoryGlyph category={category} />
					<RowName>{EVENT_CATEGORY_LABELS[category]}</RowName>
					{category === selected && (
						<RowCheck>
							<CheckIcon sx={{ fontSize: iconSizes.md }} />
						</RowCheck>
					)}
				</OptionRow>
			))}
		</SheetList>
	</Modal>
)

interface CategoryFieldProps {
	/** Wires a visible `<label htmlFor>` to the field. */
	id?: string
	value: EventCategory
	onChange: (category: EventCategory) => void
	/** Accessible name when no visible label points at the field. */
	'aria-label'?: string
}

/**
 * The category as a field: its glyph and name; clicking it opens the category
 * sheet. Shared by the create-event flow, the idea form and the event details,
 * as iOS shares its category field and sheet.
 */
const CategoryField: React.FC<CategoryFieldProps> = ({ id, value, onChange, 'aria-label': ariaLabel }) => {
	const [isOpen, setIsOpen] = useState(false)

	return (
		<>
			{/* The Dropdown's trigger — a button on the field recipe — since it too opens a single choice. */}
			<DropdownTrigger
				id={id}
				type="button"
				aria-label={ariaLabel ? `${ariaLabel}, ${EVENT_CATEGORY_LABELS[value]}` : undefined}
				aria-haspopup="dialog"
				aria-expanded={isOpen}
				onClick={() => setIsOpen(true)}
			>
				<FieldValue>
					<CategoryGlyph category={value} />
					{EVENT_CATEGORY_LABELS[value]}
				</FieldValue>
				<DropdownChevron aria-hidden $open={false}>
					<ExpandMoreIcon sx={{ fontSize: iconSizes.sm }} />
				</DropdownChevron>
			</DropdownTrigger>
			<CategorySheet isOpen={isOpen} selected={value} onSelect={onChange} onClose={() => setIsOpen(false)} />
		</>
	)
}

export default CategoryField
